import React, { useState, useEffect, useMemo } from 'react';
import { aiService } from '../../services/aiService';
import { storageService } from '../../services/storageService';
import { authService } from '../../services/authService';
import { SoAControl, Policy, PolicySection } from '../../types/iso27001';
import { GRCControl, Framework } from '../../types/grc';
import { grcService } from '../../services/grcService';
import { Loader2, Wand2, AlertCircle, FileText, Edit, Trash2, Plus } from 'lucide-react';
import { PolicyEditor } from './PolicyEditor';
import { Pagination } from '../ui/Pagination';
import { FilterBar } from '../ui/FilterBar';

import { getUniqueOptions, filterItems } from '../../utils/filterUtils';

interface PolicyGeneratorProps {
  userRole: 'admin' | 'editor' | 'viewer';
  initialSearchQuery?: string;
  onNavigate?: (tab: string, filters?: any) => void;
  frameworkId?: number | null;
  framework?: Framework | null;
}

export const PolicyGenerator: React.FC<PolicyGeneratorProps> = ({ userRole, initialSearchQuery, onNavigate, frameworkId, framework }) => {
  const [view, setView] = useState<'list' | 'generator' | 'edit'>('list');
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null);
  
  // Check permissions from authService (section-based RBAC)
  const canEdit = authService.hasPermission('policies', 'edit') || authService.isGlobalAdmin() || authService.isOrgAdmin();
  const canDelete = canEdit; // Edit permission includes delete

  // Generator State
  const [topic, setTopic] = useState('');
  const [context, setContext] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requiredPolicies, setRequiredPolicies] = useState<SoAControl[]>([]);
  const [frameworkControls, setFrameworkControls] = useState<GRCControl[]>([]);

  // Pagination & Filter
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery || '');
  
  // Update search query if prop changes
  useEffect(() => {
    if (initialSearchQuery) {
      setSearchQuery(initialSearchQuery);
    }
  }, [initialSearchQuery]);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [controlFilter, setControlFilter] = useState('');

  // Load framework-specific controls when frameworkId changes
  useEffect(() => {
    if (frameworkId) {
      grcService.getSoA(frameworkId).then(controls => {
        setFrameworkControls(controls);
        // Map GRCControls to SoAControl format for the sidebar
        const mapped: SoAControl[] = controls
          .filter(c => c.applicable === 1)
          .map(c => ({
            id: c.control_code || `ctrl-${c.id}`,
            name: c.title,
            description: c.description || '',
            applicable: c.applicable === 1,
            justification: c.applicability_justification || '',
            implementationStatus: c.status === 'compliant' ? 'implemented' : c.status === 'partial' ? 'in_progress' : 'not_started',
            owner: c.owner || '',
            fulfillmentRationale: c.fulfillment_rationale || '',
            iso27002Guidance: c.implementation_notes || '',
            coveredByPolicies: c.suggested_policy_names || [],
            implementationSteps: c.implementation_steps || [],
          }));
        setRequiredPolicies(mapped);
      }).catch(() => {
        // Fallback to local storage
        const soa = storageService.getSoA();
        setRequiredPolicies(soa.filter(c => c.applicable));
      });
    } else {
      const soa = storageService.getSoA();
      setRequiredPolicies(soa.filter(c => c.applicable));
    }
  }, [frameworkId]);

  useEffect(() => {
    if (frameworkId) {
      // Load documents/policies from DB for this framework
      grcService.getDocuments(frameworkId).then(docs => {
        const mapped: Policy[] = docs.map(d => ({
          id: String(d.id),
          title: d.title,
          sections: [],
          version: d.version || '1.0',
          createdDate: '',
          lastUpdated: '',
          nextReviewDate: d.next_review_date || '',
          status: (d.status === 'approved' ? 'approved' : d.status === 'draft' ? 'draft' : 'review') as 'draft' | 'review' | 'approved',
          generatedByAI: false,
          owner: d.owner || '',
          frameworkCode: framework?.code,
        }));
        setPolicies(mapped);
      }).catch(() => {
        // Fallback to localStorage
        setPolicies(storageService.getPolicies());
      });
    } else {
      setPolicies(storageService.getPolicies());
    }
  }, [frameworkId]);

  // Get framework control codes for filtering
  const frameworkControlCodes = useMemo(() => {
    if (frameworkControls.length > 0) {
      return new Set(frameworkControls.map(c => c.control_code || `ctrl-${c.id}`));
    }
    return null; // null means no framework filter applied
  }, [frameworkControls]);

  const filteredPolicies = useMemo(() => {
    let filtered = policies;

    // Primary filter: by frameworkCode (each policy belongs to a specific framework)
    if (framework?.code) {
      filtered = filtered.filter(p => {
        // Show policies that explicitly belong to this framework
        if (p.frameworkCode) return p.frameworkCode === framework.code;
        // Legacy policies without frameworkCode: show only if control codes match
        if (frameworkControlCodes && frameworkControlCodes.size > 0 && p.coveredControls?.length) {
          return p.coveredControls.some(cc => frameworkControlCodes.has(cc));
        }
        // Legacy policies with no controls and no frameworkCode: don't show
        return false;
      });
    }

    if (controlFilter) {
      filtered = filtered.filter(p => p.coveredControls?.includes(controlFilter));
    }

    return filterItems(filtered, searchQuery, ['title', 'owner', 'approver'], {
      status: statusFilter,
      owner: ownerFilter
    });
  }, [policies, searchQuery, statusFilter, ownerFilter, controlFilter, frameworkControlCodes, framework]);

  const paginatedPolicies = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredPolicies.slice(start, start + pageSize);
  }, [filteredPolicies, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredPolicies.length / pageSize);

  // Dynamic Options
  const ownerOptions = useMemo(() => getUniqueOptions(policies, 'owner'), [policies]);
  const controlOptions = useMemo(() => {
    const allControls = new Set<string>();
    policies.forEach(p => p.coveredControls?.forEach(c => allControls.add(c)));
    return Array.from(allControls).sort().map(c => ({ label: c, value: c }));
  }, [policies]);

  const handleGenerate = async () => {
    if (!topic || !frameworkId) return;
    setLoading(true);
    setError(null);
    try {
      // Build framework context for multi-framework AI generation
      let frameworkContext = undefined;
      if (framework && frameworkControls.length > 0) {
        frameworkContext = {
          code: framework.code,
          name: framework.name,
          frameworkType: framework.framework_type,
          controls: frameworkControls.map(c => ({
            code: c.control_code || `ctrl-${c.id}`,
            title: c.title,
            description: c.description || undefined
          }))
        };
      }
      
      const { sections, coveredControls } = await aiService.generatePolicy(topic, context, frameworkContext);
      
      // Save to database instead of localStorage
      const dbDoc = await grcService.createDocument({
        framework_id: frameworkId,
        doc_type: 'policy',
        title: topic,
        content: sections.map(s => `## ${s.title}\n\n${s.content}`).join('\n\n'),
        sections: JSON.stringify(sections),
        owner: 'TBD',
        status: 'draft',
        covered_controls: coveredControls
      });
      
      if (dbDoc) {
        // Reload policies from database
        const docs = await grcService.getDocuments(frameworkId);
        const mapped: Policy[] = docs.map(d => ({
          id: String(d.id),
          title: d.title,
          sections: d.sections ? (typeof d.sections === 'string' ? JSON.parse(d.sections) : d.sections) : [],
          version: d.version || '1.0',
          createdDate: d.created_at || '',
          lastUpdated: d.updated_at || '',
          nextReviewDate: d.next_review_date || '',
          status: (d.status === 'approved' ? 'approved' : d.status === 'draft' ? 'draft' : 'review') as 'draft' | 'review' | 'approved',
          generatedByAI: d.generated_by_ai === 1,
          owner: d.owner || '',
          approver: d.approver || '',
          frameworkCode: framework?.code,
          coveredControls: d.covered_controls ? (typeof d.covered_controls === 'string' ? JSON.parse(d.covered_controls) : d.covered_controls) : []
        }));
        setPolicies(mapped);
        
        // Find and select the newly created policy
        const newPolicy = mapped.find(p => p.id === String(dbDoc.id));
        if (newPolicy) {
          setSelectedPolicy(newPolicy);
          setView('edit');
        }
      } else {
        setError('Failed to save policy to database');
      }
    } catch (error: any) {
      console.error(error);
      setError(error.message || 'Failed to generate policy');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePolicy = async (updatedPolicy: Policy) => {
    if (!frameworkId) return;
    
    try {
      // Check if content changed to create a new version
      const existingPolicy = policies.find(p => p.id === updatedPolicy.id);
      let newVersion = updatedPolicy.version;
      
      if (existingPolicy) {
        const contentChanged = JSON.stringify(existingPolicy.sections) !== JSON.stringify(updatedPolicy.sections);
        if (contentChanged) {
          newVersion = (parseFloat(existingPolicy.version) + 0.1).toFixed(1);
        }
      }
      
      // Save to database
      await grcService.updateDocument(parseInt(updatedPolicy.id, 10), {
        title: updatedPolicy.title,
        content: updatedPolicy.sections?.map(s => `## ${s.title}\n\n${s.content}`).join('\n\n'),
        sections: JSON.stringify(updatedPolicy.sections),
        owner: updatedPolicy.owner,
        status: updatedPolicy.status,
        version: newVersion,
        covered_controls: updatedPolicy.coveredControls
      });
      
      // Reload from database
      const docs = await grcService.getDocuments(frameworkId);
      const mapped: Policy[] = docs.map(d => ({
        id: String(d.id),
        title: d.title,
        sections: d.sections ? (typeof d.sections === 'string' ? JSON.parse(d.sections) : d.sections) : [],
        version: d.version || '1.0',
        createdDate: d.created_at || '',
        lastUpdated: d.updated_at || '',
        nextReviewDate: d.next_review_date || '',
        status: (d.status === 'approved' ? 'approved' : d.status === 'draft' ? 'draft' : 'review') as 'draft' | 'review' | 'approved',
        generatedByAI: d.generated_by_ai === 1,
        owner: d.owner || '',
        approver: d.approver || '',
        frameworkCode: framework?.code,
        coveredControls: d.covered_controls ? (typeof d.covered_controls === 'string' ? JSON.parse(d.covered_controls) : d.covered_controls) : []
      }));
      setPolicies(mapped);
    } catch (error) {
      console.error('Error saving policy:', error);
    }
    
    setView('list');
    setSelectedPolicy(null);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure?')) {
      await grcService.deleteDocument(parseInt(id, 10));
      if (frameworkId) {
        const docs = await grcService.getDocuments(frameworkId);
        const mapped: Policy[] = docs.map(d => ({
          id: String(d.id),
          title: d.title,
          sections: d.sections ? (typeof d.sections === 'string' ? JSON.parse(d.sections) : d.sections) : [],
          version: d.version || '1.0',
          createdDate: d.created_at || '',
          lastUpdated: d.updated_at || '',
          nextReviewDate: d.next_review_date || '',
          status: (d.status === 'approved' ? 'approved' : d.status === 'draft' ? 'draft' : 'review') as 'draft' | 'review' | 'approved',
          generatedByAI: d.generated_by_ai === 1,
          owner: d.owner || '',
          frameworkCode: framework?.code,
          coveredControls: d.covered_controls ? (typeof d.covered_controls === 'string' ? JSON.parse(d.covered_controls) : d.covered_controls) : []
        }));
        setPolicies(mapped);
      }
    }
  };

  const selectRequirement = (control: SoAControl) => {
    const fwName = framework?.name || framework?.code || 'ISO 27001';
    setTopic(`Policy for ${control.name}`);
    let contextText = `Required for ${fwName} Control ${control.id}: ${control.description}.\nJustification: ${control.justification}`;
    if (control.fulfillmentRationale) {
      contextText += `\n\nOrganization's Fulfillment Approach: ${control.fulfillmentRationale}`;
    }
    setContext(contextText);
  };

  if (view === 'edit' && selectedPolicy) {
    return (
      <PolicyEditor 
        policy={selectedPolicy} 
        onSave={handleSavePolicy} 
        onCancel={() => setView('list')} 
        onNavigate={onNavigate}
        framework={framework}
        frameworkControls={frameworkControls}
      />
    );
  }

  if (view === 'generator') {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <AlertCircle className="w-5 h-5 text-orange-600" />
            Required Policies ({framework?.code || 'SoA'})
          </h3>
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {requiredPolicies.map(control => (
              <button
                key={control.id}
                onClick={() => selectRequirement(control)}
                className="w-full text-left p-3 rounded border border-gray-200 dark:border-gray-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 hover:border-indigo-200 dark:hover:border-indigo-700 transition-colors text-sm"
              >
                <div className="font-medium text-gray-800 dark:text-gray-200">{control.id} {control.name}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2 text-gray-900 dark:text-gray-100">
              <Wand2 className="w-5 h-5 text-indigo-600" />
              AI Policy Generator
            </h3>
            <button onClick={() => setView('list')} className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">Cancel</button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Policy Topic</label>
              <input
                type="text"
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Context</label>
              <textarea
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                rows={4}
                value={context}
                onChange={(e) => setContext(e.target.value)}
              />
            </div>

            <button
              onClick={handleGenerate}
              disabled={loading || !topic}
              className="w-full flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {loading ? 'Generating...' : 'Generate & Edit'}
            </button>
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md text-sm text-red-600 dark:text-red-300 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex-1 w-full">
          <FilterBar 
            onSearch={setSearchQuery}
            filters={[
              {
                label: 'Status',
                value: statusFilter,
                onChange: setStatusFilter,
                options: [
                  { label: 'Draft', value: 'draft' },
                  { label: 'Review', value: 'review' },
                  { label: 'Approved', value: 'approved' },
                ]
              },
              {
                label: 'Owner',
                value: ownerFilter,
                onChange: setOwnerFilter,
                options: ownerOptions
              },
              {
                label: 'Control',
                value: controlFilter,
                onChange: setControlFilter,
                options: controlOptions
              }
            ]}
          />
        </div>
        {canEdit && (
          <button onClick={() => setView('generator')} className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 whitespace-nowrap">
            <Plus className="w-4 h-4" /> New Policy
          </button>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Title</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Owner</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Version</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Last Updated</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Next Review</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {paginatedPolicies.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center">
                  <div className="flex flex-col items-center gap-2 text-gray-500 dark:text-gray-400">
                    <FileText className="w-8 h-8 opacity-40" />
                    <p className="font-medium">No policies for {framework?.name || 'this framework'}</p>
                    <p className="text-sm">Click "New Policy" to create framework-specific policies based on applicable controls.</p>
                  </div>
                </td>
              </tr>
            ) : paginatedPolicies.map((policy) => {
              const openFindings = storageService.getOpenFindingsForItem(policy.id);
              return (
                <tr 
                  key={policy.id} 
                  onClick={() => { setSelectedPolicy(policy); setView('edit'); }}
                  className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-400" />
                      {policy.title}
                      {openFindings.length > 0 && (
                        <span 
                          className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 cursor-help" 
                          title={`Open Findings:\n${openFindings.map(f => `• ${f.description} (${f.type})`).join('\n')}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            alert(`Open Findings for ${policy.title}:\n\n${openFindings.map(f => `• [${f.type.toUpperCase()}] ${f.description}\n  Recommendation: ${f.recommendation}`).join('\n\n')}`);
                          }}
                        >
                          <AlertCircle className="w-3 h-3 mr-1" /> {openFindings.length}
                        </span>
                      )}
                    </div>
                    {policy.coveredControls && policy.coveredControls.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {policy.coveredControls.slice(0, 3).map(c => (
                          <span key={c} className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-1.5 py-0.5 rounded border border-gray-200 dark:border-gray-600">
                            {c}
                          </span>
                        ))}
                        {policy.coveredControls.length > 3 && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">+{policy.coveredControls.length - 3} more</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{policy.owner || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{policy.version}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{policy.createdDate ? new Date(policy.createdDate).toLocaleDateString() : '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{new Date(policy.lastUpdated).toLocaleDateString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{policy.nextReviewDate}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {canEdit && (
                      <button onClick={() => { setSelectedPolicy(policy); setView('edit'); }} className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 mr-4"><Edit className="w-4 h-4" /></button>
                    )}
                    {canDelete && (
                      <button onClick={() => handleDelete(policy.id)} className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        totalItems={filteredPolicies.length}
      />
    </div>
  );
};
